/**
 * OAuthCallback - Handles OAuth redirect in popup window
 *
 * v4.19 - 2026-09-21 - AIRBYTE-BRIDGE-PLATFORM-CREDENTIALS S3 (F04): read `error`
 *   before `success`. On error, post {type:'oauth_error', error} to the opener
 *   (popup) and show a failure message — never post oauth_success. Outside a popup,
 *   stay on the page and show the failure message with a link back to setup (the
 *   onboarding page reads only `success`, so navigating there would hide the error).
 *   The success path (popup and non-popup) is unchanged.
 * v2.84 - 2026-02-05 - Fix postMessage origin mismatch (www.mylojic.com vs mylojic.com)
 * v2.80 - 2026-02-05 - Initial implementation for popup-based OAuth flow
 *
 * When OAuth completes, the provider return lands here with either
 * ?success={connector}&connection={id}  (success) or ?error={code} (failure).
 *
 * If running in a popup (window.opener exists):
 *   - success: postMessage {type:'oauth_success', ...} to the parent, then close
 *   - error:   postMessage {type:'oauth_error', error} to the parent, show failure
 *
 * If NOT in a popup (e.g., direct navigation or popup blocked):
 *   - success: redirect to /onboarding?success={connector} (legacy behavior)
 *   - error:   stay on this page, show the failure message + a link back to setup
 *
 * v2.84 NOTE: postMessage uses '*' target origin because the parent may be on
 * mylojic.com while the popup is on www.mylojic.com. This is safe: we only send to
 * window.opener, which is always our own parent window.
 */
import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Typography, Link as MuiLink } from '@mui/material';

const OAuthCallback: React.FC = () => {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    // v4.19 - read error BEFORE success so a failure return is never treated as success.
    const error = params.get('error');
    const connector = params.get('success');
    const connectionId = params.get('connection');

    if (error) {
      // Failure return.
      if (window.opener) {
        try {
          window.opener.postMessage(
            {
              type: 'oauth_error',
              error: error,
            },
            '*'
          );
          console.log('v4.19: oauth_error posted to opener:', error);
        } catch (err) {
          console.error('OAuthCallback: Failed to postMessage oauth_error to opener:', err);
        }
        // Close the popup after a short delay to ensure the message is sent.
        setTimeout(() => window.close(), 500);
      }
      // Whether or not there is an opener, show the failure on this page. Do NOT
      // navigate to /onboarding — that page reads only `success` and would hide it.
      setFailed(true);
      return;
    }

    // Success return (unchanged behavior).
    if (window.opener) {
      try {
        window.opener.postMessage(
          {
            type: 'oauth_success',
            connector: connector,
            connectionId: connectionId,
          },
          '*'
        );
        console.log('v2.84: postMessage sent to opener with *');
      } catch (err) {
        console.error('OAuthCallback: Failed to postMessage to opener:', err);
      }
      setTimeout(() => window.close(), 500);
    } else {
      const redirectUrl = connector
        ? `/onboarding?success=${connector}${connectionId ? `&connection=${connectionId}` : ''}`
        : '/onboarding';
      window.location.href = redirectUrl;
    }
  }, []);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        bgcolor: 'background.default',
        gap: 2,
        px: 2,
        textAlign: 'center',
      }}
    >
      {failed ? (
        <>
          <Typography variant="h6" color="text.primary">
            We couldn't finish connecting your account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Something went wrong during sign-in and no connection was saved. Please
            close this window and try again from the setup page.
          </Typography>
          <MuiLink href="/onboarding" underline="hover">
            Back to setup
          </MuiLink>
        </>
      ) : (
        <>
          <CircularProgress color="primary" />
          <Typography variant="body1" color="text.secondary">
            Connection successful! Closing...
          </Typography>
        </>
      )}
    </Box>
  );
};

export default OAuthCallback;
